/**
 * <strong>Purpose:</strong> <br>
 * Provides the ability to hide/show the text of the widget. When the text is hidden, it appears in the tooltip instead
 * of the description.
 * <br><br>
 * <strong>Contract:</strong> <br>
 * This works in conjunction with the TextMixin, DescriptionMixin and ShortcutMixin. DO mix this in after the TextMixin,
 * DescriptionMixin and ShortcutMixin.
 *
 * @module MW/toolstrip/mixins/property/ShowTextMixin
 *
 * @copyright Copyright 2015-2020 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "MW/toolstrip/constants/MiscConstants",
    "MW/toolstrip/mixins/property/TooltipMixin"
], function (declare, MiscConstants, TooltipMixin) {

    function matchesSelector (/*Element*/ element, /*String*/ selector) { // TODO: use domUtils.matchesSelector instead
        var matchesMethod;

        if (!element || !selector) {
            return false;
        }

        matchesMethod = 
            Element.prototype.matches || 
            Element.prototype.matchesSelector || 
            Element.prototype.mozMatchesSelector ||
            Element.prototype.msMatchesSelector || 
            Element.prototype.oMatchesSelector || 
            Element.prototype.webkitMatchesSelector ||
            function(s) {
                var matches = (this.document || this.ownerDocument).querySelectorAll(s),
                    i = matches.length;
                while (--i >= 0 && matches.item(i) !== this) {}
                return i > -1;            
            };

        return matchesMethod.call(element, selector);
    };

    var ShowTextMixin = declare(null, {

        /**
         * @property  {boolean} showText
         * @instance
         */
        showText: true,

        _setTextAttr: function () {
            this.inherited(arguments);
            this._placeTextInNode(this.get("text"), this.textNode || this.domNode);
        },

        _setShowTextAttr: function (showText) {
            if (typeof showText !== "boolean") {
                throw new Error("'showText' property expects a boolean!");
            }

            if (!this._restrictModelUpdate) {
                this._set("showText", showText);
            } else {
                // To avoid updating the model. Need this for QAB overflow use case where we want to always show the label
                // in the overflow popup irrespective of 'showText' property of the widget.
                this.showText = showText;
            }

            if (this.get("text") !== "") {
                this.domNode.classList.toggle("mwNoText", !showText);
            }

            this._placeTextInNode(this.get("text"), this.textNode || this.domNode);
            this._updateTooltip(showText);
        },

        _updateTooltip: function () {
            if (matchesSelector(this.domNode, ".compactToolstrip *")) {
                this._updateTitleInDOM("");
                return;
            }
        
            if (this.showText) {
                this._excludeTextInTooltip = true;
            } else {
                this._excludeDescriptionInTooltip = true;
            }
            
            this.inherited(arguments);
            
            delete this._excludeTextInTooltip;
            delete this._excludeDescriptionInTooltip;
        },
        
        _updateDataTextContent: function (text, node, force) {
            if (this.showText) {
                (node || this.textNode || this.domNode).setAttribute("data-text-content", force ? text : this._getTextForDOM(text, node));
            } else {
                (node || this.textNode || this.domNode).setAttribute("data-text-content", "");
            }
        },

        /* Override method in textOverrideMixin */
        _updateNoTextClass: function () {
            if ((!this.showText) || ((this.get("textOverride") === "") && (this.get("text") === "")) || (this.get("textOverride") === MiscConstants.DO_NOT_DISPLAY) ) {
                this.domNode.classList.add("mwNoText");
            } else {
                this.domNode.classList.remove("mwNoText");
            }
        }
    });

    ShowTextMixin._dependencies = [
        {mixin: TooltipMixin, orderDependent: true}
    ];

    return ShowTextMixin;

});
